import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Plane, Globe2, ChevronDown, Search, X, Phone } from "lucide-react";
import { api } from "@/lib/api";
import { RouteCard } from "./RouteCard";
import { AnyRouteCard } from "./AnyRouteCard";
import { PHONE_TEL } from "@/lib/site";
import { SectionHead } from "@/components/layout/Motion";
import { TextLink } from "@/components/layout/CallButton";

const TABS = [
  { key: "domestic", label: "USA Domestic", icon: Plane },
  { key: "international", label: "International", icon: Globe2 },
];
const PAGE = 11;

export const RouteSections = () => {
  const [routes, setRoutes] = useState([]);
  const [tab, setTab] = useState("domestic");
  const [limit, setLimit] = useState(PAGE);
  const [q, setQ] = useState("");
  useEffect(() => {
    api.get("/routes").then((r) => setRoutes(Array.isArray(r.data) ? r.data : [])).catch(() => setRoutes([]));
  }, []);

  const hotIds = useMemo(() => new Set(routes.filter((r) => r?.hot).map((r) => r.id)), [routes]);
  const query = q.trim().toLowerCase();
  const list = query
    ? routes.filter((r) => `${r.origin_city} ${r.origin_code} ${r.destination_city} ${r.destination_code}`.toLowerCase().includes(query))
    : routes.filter((r) => r?.category === tab);
  list.sort((a, b) => Number(!!b.hot) - Number(!!a.hot));
  const pick = (k) => { setTab(k); setLimit(PAGE); setQ(""); };

  return (
    <section id="flight-routes" className="bg-sand py-16" data-testid="route-sections">
      <div className="mx-auto max-w-[1320px] px-4 sm:px-8">
        <SectionHead eyebrow="Flight deals from U.S. airports" title="Find your next flight." text="Explore featured domestic and international airline tickets. Don’t see your destination? Call our team to check options for your route and dates."
          action={<TextLink testId="talk-travel-team">Talk to our travel team</TextLink>} />

        <div className="mt-8 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="inline-flex w-fit rounded-full border border-slate-200 bg-white p-1" role="tablist" data-testid="route-tabs">
          {TABS.map(({ key, label, icon: Icon }) => {
            const active = !query && tab === key;
            const count = routes.filter((r) => r?.category === key).length;
            return (
              <button key={key} onClick={() => pick(key)} role="tab" aria-selected={active} data-testid={`route-tab-${key}`}
                className={`relative flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-2 text-[13px] font-bold transition-colors sm:gap-2 sm:px-5 sm:text-sm ${active ? "text-white" : "text-ink hover:text-brand"}`}>
                {active && <motion.span layoutId="route-tab-pill" className="absolute inset-0 rounded-full bg-ink" transition={{ type: "spring", stiffness: 380, damping: 30 }} />}
                <Icon className="relative h-4 w-4" />
                <span className="relative">{label}</span>
                <span className={`relative rounded-full px-1.5 text-[10px] ${active ? "bg-coral text-white" : "bg-slate-100 text-slate-500"}`}>{count}</span>
              </button>
            );
          })}
        </div>
          <label className="group relative flex w-full items-center lg:w-[380px]">
            <Search className="pointer-events-none absolute left-4 h-4 w-4 text-slate-400 transition-colors group-focus-within:text-brand" />
            <input value={q} onChange={(e) => { setQ(e.target.value); setLimit(PAGE); }} placeholder="Search any city or airport code…" data-testid="route-search-input"
              className="w-full rounded-full border border-slate-200 bg-white py-3 pl-11 pr-10 text-sm text-ink outline-none transition-[border-color,box-shadow] focus:border-brand focus:shadow-[0_0_0_4px_rgba(21,101,216,0.12)]" />
            {q && <button onClick={() => setQ("")} aria-label="Clear search" data-testid="route-search-clear" className="absolute right-3 grid h-6 w-6 place-items-center rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200"><X className="h-3.5 w-3.5" /></button>}
          </label>
        </div>

        {query && (
          <p className="mt-4 text-sm text-slate-600" data-testid="route-search-summary">
            {list.length ? `${list.length} featured fare${list.length > 1 ? "s" : ""} found for “${q.trim()}”.` : `No featured fare for “${q.trim()}” yet. Ask us to check flight options.`}{" "}
            <a href={PHONE_TEL} data-testid="route-search-call" className="inline-flex items-center gap-1 font-bold text-coral"><Phone className="h-3.5 w-3.5" />Call for today’s fare</a>
          </p>
        )}

        <div key={query ? "search" : tab} className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid={query ? "search-routes" : `${tab}-routes`}>
          {list.slice(0, limit).map((r, i) => <RouteCard key={r.id} route={r} index={i} hot={hotIds.has(r.id)} />)}
          <AnyRouteCard query={query && !list.length ? q.trim() : ""} />
        </div>

        {list.length > limit && (
          <div className="mt-6 flex justify-center">
            <button onClick={() => setLimit(list.length)} data-testid="routes-show-all"
              className="group flex items-center gap-2 rounded-full border border-slate-300 bg-white px-5 py-2.5 text-sm font-bold text-ink transition-colors hover:border-brand hover:text-brand">
              Show all {list.length} routes <ChevronDown className="h-4 w-4 transition-transform group-hover:translate-y-0.5" />
            </button>
          </div>
        )}

        <p className="mx-auto mt-10 max-w-4xl text-center text-xs leading-relaxed text-slate-500" data-testid="fares-disclaimer">
          Estimated agency starting prices use public one-way economy references for the next three months and an indicative agency adjustment. They are not live availability or guaranteed quotes.
          City-area airports and connections may apply; airline options are illustrative. Optional baggage and service charges may apply. Confirm your dates, total fare and availability on the call.
        </p>
        <p className="mt-2 text-center text-[11px] text-slate-500">Currency conversions are indicative. <a href="https://www.exchangerate-api.com" target="_blank" rel="noreferrer" className="underline">Rates by Exchange Rate API</a>.</p>
      </div>
    </section>
  );
};
